import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerTitle } from '@/components/ui/drawer';
import { useClientTranslation } from '@/lib/core';
import { initializeTalkdeskChat } from '@/lib/core/utils';

export interface RequestPrivateEventSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Sheet opened from the ELP's "add event" (+) button, informing the user that
 * private events are handled by the concierge.
 *
 * "Chat with concierge" only opens the Talkdesk chat — it does not create the
 * Salesforce Lead the events contract describes (§3.6 `POST /event-requests`),
 * because that endpoint is not exposed by the platform API yet, and passes no
 * context identifying the request.
 */
export function RequestPrivateEventSheet({ open, onOpenChange }: RequestPrivateEventSheetProps) {
  const t = useClientTranslation();

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle={false}>
      <DrawerContent className="bg-[#1a1a1a]">
        <div className="flex flex-1 flex-col gap-4 px-4 pt-10 pb-4">
          <DrawerTitle className="font-pp-neue-montreal text-[24px] leading-tight font-semibold text-white">
            {t('App.Events.RequestPrivateEventTitle')}
          </DrawerTitle>
          <DrawerDescription className="font-pp-neue-montreal text-[16px] leading-normal tracking-[0.25px] text-[#eaeaea]">
            {t('App.Events.RequestPrivateEventDescription')}
          </DrawerDescription>
        </div>

        <DrawerFooter className="flex shrink-0 flex-col gap-2 border-t border-[#c8c8c8]/30 px-4 pt-6 pb-10">
          <Button
            variant="primary-solid"
            palette="white"
            size="lg"
            className="w-full"
            onClick={() => {
              onOpenChange(false);
              initializeTalkdeskChat();
            }}
          >
            {t('App.Events.ChatWithConcierge')}
          </Button>
          <Button
            variant="tertiary-link"
            palette="white"
            size="sm"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            {t('App.Labels.Cancel')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
